import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Menu, Mail, FolderClosed, Clock, UserRound, LogOut, SunMoon, SlidersHorizontal, Download, Shield, ScrollText, Home, UserCog, X, DollarSign, LifeBuoy, BookOpen, Search, MessageCircle, TrendingUp, Globe, Contact } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import ChatPanel from "@/components/dashboard/chat/ChatPanel";
import ChatHeads from "@/components/dashboard/chat/ChatHeads";
import CallManager from "@/components/dashboard/chat/CallManager";
import LiveViewRecorder from "@/components/dashboard/LiveViewRecorder";
import { resetPageZoom, useVisualViewport } from "@/components/dashboard/chat/useVisualViewport";
import { toast } from "sonner";
import ProjectsTab from "@/components/dashboard/ProjectsTab";
import ContactsTab from "@/components/dashboard/ContactsTab";
import TakeoffTab from "@/components/dashboard/takeoff/TakeoffTab";
import DesignTab from "@/components/dashboard/takeoff/DesignTab";
import EstimateTab from "@/components/dashboard/EstimateTab";
import BillingTab from "@/components/dashboard/BillingTab";
import EmployeesTab from "@/components/dashboard/EmployeesTab";
import EmailTab from "@/components/dashboard/EmailTab";
import HoursTab from "@/components/dashboard/HoursTab";
import SearchTab from "@/components/dashboard/SearchTab";
import LeadsModPage from "@/pages/leads-mod/LeadsPage";
import PayrollTab from "@/components/dashboard/PayrollTab";
import BrowserTab from "@/components/dashboard/browser/BrowserTab";
import { BrowserAccessContext } from "@/components/dashboard/browser/OpenInBrowser";
import FilesHub from "@/components/dashboard/FilesHub";
import ExpensesTab from "@/components/dashboard/ExpensesTab";
import HomeTab from "@/components/dashboard/HomeTab";
import AccountingTab from "@/components/dashboard/accounting/AccountingTab";
import SystemFolderBar from "@/components/dashboard/SystemFolderBar";
import CustomerPortal from "@/components/dashboard/CustomerPortal";
import SettingsDialog from "@/components/dashboard/SettingsDialog";
import ThemeSettingsDialog from "@/components/dashboard/ThemeSettingsDialog";
import OfflineBanner from "@/components/dashboard/OfflineBanner";
import HelpDialog from "@/components/dashboard/HelpDialog";
import QuickBooksTab from "@/components/dashboard/QuickBooksTab";
import ImpersonationBanner from "@/components/dashboard/ImpersonationBanner";
import WorkgroupBar, { useWorkgroup } from "@/components/dashboard/WorkgroupBar";
import { getRecent, rememberImpersonation, forgetImpersonation } from "@/lib/recentImpersonations";
import { openInstallPrompt, isInstalled } from "@/components/InstallPrompt";
import { ComposeProvider } from "@/hooks/useCompose";

const LOGO = "/api/branding/icon/elite-logo-dark.png";
const LOGO_LIGHT = "/api/branding/icon/elite-logo-light.png";
const TAB_DEFS = [
  { key: "projects", label: "Projects" },
  { key: "expenses", label: "Expenses" },
  { key: "billing", label: "Billing" },
  { key: "accounting", label: "Accounting" },
  // Single Estimate tab — the old Estimate workspace and the new takeoff
  // workspace (formerly "Estimating 2") live behind an Old/New toggle.
  { key: "estimate", label: "Estimate" },
  // Design = the CAD (DWG/DXF) workspace. Same takeoff engine, but only
  // design-flagged jobs; gated by the per-user "Design" checkbox.
  { key: "design", label: "Design" },
];
// Contacts moved out of the tab bar into the settings dropdown (user decision 2026-11-01);
// it still routes through the same TabsContent.

const Dashboard = () => {
  const { user, logout, checkAuth } = useAuth();
  const { applyWorkgroupDefault, applyPush } = useTheme();
  const wg = useWorkgroup();
  useEffect(() => {
    api.get("/theme").then((r) => { applyWorkgroupDefault(r.data?.default_theme); applyPush(r.data?.push); }).catch(() => {});
  }, [applyWorkgroupDefault, applyPush]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // In-app browser: owners/admins by default, others when ticked in Admin settings → Permissions.
  // Comes with /auth/me so the header renders complete on first paint; re-checked when the user changes.
  const [browserEnabled, setBrowserEnabled] = useState(() => (user?.browser_enabled === undefined ? null : !!user.browser_enabled));
  useEffect(() => {
    if (user?.browser_enabled !== undefined) { setBrowserEnabled(!!user.browser_enabled); return; }
    api.get("/browser/access").then((r) => setBrowserEnabled(!!r.data.enabled)).catch(() => setBrowserEnabled(false));
  }, [user?.user_id, user?.browser_enabled]);
  useEffect(() => {
    const h = () => api.get("/browser/access").then((r) => setBrowserEnabled(!!r.data.enabled)).catch(() => {});
    window.addEventListener("elite:browser-access-changed", h);
    return () => window.removeEventListener("elite:browser-access-changed", h);
  }, []);
  const [themeOpen, setThemeOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pendingProjectId, setPendingProjectId] = useState(null);
  const [recentImps, setRecentImps] = useState(() => getRecent());
  const [impersonatingId, setImpersonatingId] = useState(null);
  // Small red dot on Vendors tab whenever the duplicate-business scanner
  // finds something to tidy up. Poll on mount + every 60s so admins get a
  // fresh nudge without hammering the endpoint.
  const [dupCount, setDupCount] = useState(0);
  const [estimateVersion, setEstimateVersion] = useState("2");
  // Superadmin-only personal preview switch: flips between the old and new
  // Estimate UI without touching the company-wide setting. Per-browser.
  const [estimateOverride, setEstimateOverrideState] = useState(
    () => localStorage.getItem("estimate_version_override") || null);
  const setEstimateOverride = (v) => {
    setEstimateOverrideState(v);
    setAiEstimateRequest(null);
    try { localStorage.setItem("estimate_version_override", v); } catch { /* private mode */ }
  };
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    api.get("/settings/features")
      .then((r) => { if (alive && r.data?.estimate_version) setEstimateVersion(String(r.data.estimate_version)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user]);
  useEffect(() => {
    if (!user?.manage_users) return undefined;
    let alive = true;
    const check = () => api
      .get("/expenses/businesses/duplicates")
      .then((r) => { if (alive) setDupCount((r.data || []).length); })
      .catch(() => {});
    check();
    const id = setInterval(check, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [user?.manage_users]);

  // Silent engagement beacon — no UI, no indication anywhere. Any real
  // interaction sends a throttled beat; the server stops counting after
  // 5 quiet minutes and resumes on the next interaction.
  useEffect(() => {
    if (!user) return undefined;
    let last = 0;
    const beat = () => {
      const now = Date.now();
      if (now - last < 60000) return;
      last = now;
      api.post("/activity/beat").catch(() => {});
    };
    const evts = ["pointerdown", "keydown", "touchstart", "scroll"];
    evts.forEach((e) => window.addEventListener(e, beat, { passive: true, capture: true }));
    return () => evts.forEach((e) => window.removeEventListener(e, beat, { capture: true }));
  }, [user]);
  const refreshRecent = useCallback(() => setRecentImps(getRecent()), []);
  const quickImpersonate = useCallback(
    async (u) => {
      setImpersonatingId(u.user_id);
      try {
        const res = await api.post(`/auth/impersonate/${u.user_id}`);
        rememberImpersonation(res.data?.user || u);
        toast.success(`Now viewing as ${u.name || u.email}`);
        // Full reload so every tab re-renders with the target user's role.
        window.location.reload();
      } catch (e) {
        if (e?.response?.status === 404) {
          forgetImpersonation(u.user_id);
          refreshRecent();
        }
        toast.error(e?.response?.data?.detail || "Impersonation failed");
        setImpersonatingId(null);
      }
    },
    [refreshRecent]
  );

  const perms = useMemo(() => {
    const p = user?.permissions || {};
    // "contacts" is a virtual permission — a user sees it if they can see
    // either Customers or Vendors. The backend perm model still uses the
    // two granular keys for row-level access.
    // "estimate"/"estimating2" (merged Estimate tab): owners, superadmins
    // and admins only, plus anyone whose linked employee job title contains
    // "estimator". Managers, staff, office trades and the legacy permission
    // checkboxes no longer grant it (user decision 2026-09-30).
    const jobTitle = (user?.employee_job_title || "").trim().toLowerCase();
    const estimate = !!(
      // managers get the Estimate tab in VIEW-ONLY mode (no prices, no
      // editing — enforced server-side in routes/takeoff.py)
      ["owner", "superadmin", "admin", "manager", "viewer"].includes(user?.role) || jobTitle.includes("estimator")
    );
    // Email is core to the portal: owner/superadmin always see the envelope
    // button, even if the permissions checkbox was accidentally saved off.
    const email = !!(p.email || ["owner", "superadmin"].includes(user?.role));
    return { ...p, contacts: !!(p.customers || p.vendors), estimating2: estimate, estimate, email };
  }, [user]);
  const role = user?.role || "new_user";
  // Database (master search) — owner/superadmin/admin by default, with a
  // per-user kill switch in Admin Settings → Users ("Database" checkbox).
  // `!== false` keeps it on for sessions cached before the key existed.
  const canDatabase =
    (role === "superadmin" || role === "admin" || role === "owner") && perms.database !== false;
  const linkedEmployeeId = user?.linked_employee_id || "";
  const visibleTabs = useMemo(() => TAB_DEFS.filter((t) => perms[t.key]), [perms]);
  const hasAnyAccess = visibleTabs.length > 0 || perms.email || perms.files || perms.employees || !!linkedEmployeeId || role === "superadmin";
  const [installed, setInstalled] = useState(() => isInstalled());
  useEffect(() => {
    // On login / dashboard mount, always start scrolled to the very top so
    // the header (logo + top-bar icons + primary tabs) is visible. Without
    // this, mobile browsers restore the previous scroll position which
    // dumps the user mid-page.
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      // Some mobile browsers restore scroll asynchronously – nudge again on
      // the next paint tick so we still end up at the top.
      requestAnimationFrame(() => window.scrollTo(0, 0));
    }
  }, []);

  useEffect(() => {
    const mq = window.matchMedia?.("(display-mode: standalone)");
    if (!mq) return;
    const on = () => setInstalled(isInstalled());
    mq.addEventListener?.("change", on);
    window.addEventListener("appinstalled", on);
    return () => {
      mq.removeEventListener?.("change", on);
      window.removeEventListener("appinstalled", on);
    };
  }, []);

  // Stats is the home page for everyone — no matter the role. Other tabs are
  // still permission-gated and only show up if the user has access.
  const firstTab = "stats";

  const accessibleTabs = useMemo(() => {
    const keys = ["stats", ...visibleTabs.map((t) => t.key)];
    if (perms.contacts) keys.push("contacts");
    if (perms.hours) keys.push("hours");
    if (perms.hours || canDatabase) keys.push("search");
    if (perms.payroll) keys.push("payroll");
    if (perms.employees) keys.push("employees");
    if (perms.files) keys.push("files");
    if (perms.email) keys.push("email");
    if (role === "superadmin" || perms.billing) keys.push("quickbooks");
    if (perms.leads) keys.push("leads");
    if (perms.accounting) keys.push("accounting");
    if (browserEnabled) keys.push("browser");
    return keys;
  }, [visibleTabs, perms, role, browserEnabled]);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlTab = searchParams.get("tab");
  const [unreadMail, setUnreadMail] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMin, setChatMin] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [chatHeads, setChatHeads] = useState([]); // conversations with unread messages
  const [chatTarget, setChatTarget] = useState(null); // conversation_id to jump into
  const chatVV = useVisualViewport();

  // Open the chat dock on a specific conversation. Triggers: tapping a chat
  // notification (in-app event), tapping a phone push (SW postMessage), or
  // landing on /dashboard?chat=<id> from a push cold-start.
  useEffect(() => {
    const openOn = (cid) => {
      if (!cid) return;
      setChatTarget(cid);
      setChatOpen(true);
      setChatMin(false);
    };
    const onEvent = (e) => openOn(e.detail?.conversation_id);
    const onSwMessage = (e) => {
      if (e.data?.type === "open-chat") openOn(e.data.conversation_id);
    };
    window.addEventListener("elite:open-chat", onEvent);
    navigator.serviceWorker?.addEventListener("message", onSwMessage);
    const cid = searchParams.get("chat");
    if (cid) {
      openOn(cid);
      searchParams.delete("chat");
      setSearchParams(searchParams, { replace: true });
    }
    return () => {
      window.removeEventListener("elite:open-chat", onEvent);
      navigator.serviceWorker?.removeEventListener("message", onSwMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Blue unread dot on the Chat icon + the bottom-of-screen chat heads —
  // everyone gets chat, polls lightly.
  useEffect(() => {
    let stop = false;
    const tick = () =>
      api.get("/chat/unread")
        .then((r) => { if (!stop) { setChatUnread(r.data.conversations || 0); setChatHeads(r.data.items || []); } })
        .catch(() => {});
    refreshChatUnread.current = tick;
    tick();
    const t = setInterval(tick, 10000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  const refreshChatUnread = useRef(() => {});

  const openChatHead = (cid) => {
    setChatHeads((h) => h.filter((x) => x.conversation_id !== cid));
    setChatTarget(cid);
    setChatOpen(true);
    setChatMin(false);
  };

  // Red unread-count dot on the Mail icon — polls the cached backend count.
  useEffect(() => {
    if (!perms.email) return undefined;
    let stop = false;
    const tick = () =>
      api.get("/email/unread-count")
        .then((r) => { if (!stop) setUnreadMail(r.data.unread || 0); })
        .catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => { stop = true; clearInterval(t); };
  }, [perms.email]);
  // Legacy ?tab=customers / ?tab=vendors links stashed into deep-links land
  // on the new combined Contacts tab and pre-select the correct sub-view.
  const legacyContactsView = urlTab === "customers" ? "customers" : urlTab === "vendors" ? "vendors" : null;
  const [activeTab, setActiveTab] = useState(() => (legacyContactsView ? "contacts" : urlTab || firstTab));

  // The URL (?tab=) is the source of truth so the browser Back/Forward buttons
  // move between tabs instead of leaving the app.
  const changeTab = useCallback(
    (key) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("tab", key);
          return p;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );
  // "Send to Estimate" (takeoff Plans panel): jump to the Estimate tab on the
  // AI version — a one-shot request that does not change the company setting.
  const [aiEstimateRequest, setAiEstimateRequest] = useState(null);
  useEffect(() => {
    const h = (e) => { setAiEstimateRequest(e.detail?.projectId || true); changeTab("estimate"); };
    window.addEventListener("elite:open-ai-estimate", h);
    return () => window.removeEventListener("elite:open-ai-estimate", h);
  }, [changeTab]);

  // Sync the visible tab from the URL (covers Back/Forward and permission gating).
  useEffect(() => {
    if (!user) return; // session still loading — never redirect deep links early
    // Legacy customers/vendors URLs quietly become "contacts"; the retired
    // "estimating2" tab is the Estimate tab's New view now.
    const normalized = urlTab === "customers" || urlTab === "vendors" ? "contacts"
      : urlTab === "estimating2" ? "estimate" : urlTab;
    if (normalized === "browser" && browserEnabled === null) return; // access check still loading
    const target = normalized && accessibleTabs.includes(normalized) ? normalized : firstTab;
    if (target !== activeTab) setActiveTab(target);
    if (urlTab !== target) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("tab", target);
          return p;
        },
        { replace: true }
      );
    }
  }, [user, urlTab, accessibleTabs, firstTab, activeTab, setSearchParams, browserEnabled]);

  const openProject = (projectId) => {
    setPendingProjectId(projectId);
    changeTab("projects");
  };

  const tabCls =
    "min-w-0 flex-1 truncate rounded-none border-b-2 border-transparent px-1 py-3 font-mono text-[10px] uppercase tracking-tight text-white/50 data-[state=active]:border-[#1a3fd1] data-[state=active]:bg-transparent data-[state=active]:text-[#4f6fe8] data-[state=active]:shadow-none sm:px-3 sm:text-xs sm:tracking-[0.12em] lg:px-5 lg:tracking-[0.2em]";
  const iconBtn = (active) =>
    `flex h-8 w-8 items-center justify-center rounded-md border transition-colors duration-300 ${
      active ? "border-[#1a3fd1] bg-[#1a3fd1]/15 text-white" : "border-white/15 text-white/70 hover:border-[#1a3fd1] hover:text-white"
    }`;

  // ---- New user (no access): blank app + contact message + sign out ----
  // Linked employees always get access (at least to the Stats tab), so bypass this gate.
  if (!linkedEmployeeId && (role === "new_user" || role === "user" || (role === "customer" && !user?.customer_project_id) || !hasAnyAccess)) {
    if (role !== "customer" || !user?.customer_project_id) {
      const isCustomerWaiting = role === "customer";
      return (
        <div className="flex h-screen flex-col bg-[#050505] text-white" data-testid="dashboard">
          <ImpersonationBanner />
        <WorkgroupBar />
          <header id="app-header" className="flex shrink-0 items-center justify-between border-b border-white/10 bg-[#0a0a0a] px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6">
            <img src={LOGO} alt="Elite Mechanical" className="logo-dark-only h-9 w-auto object-contain" data-testid="topbar-logo" />
            <img src={LOGO_LIGHT} alt="Elite Mechanical" className="logo-light-only h-9 w-auto object-contain" data-testid="topbar-logo-light" />
            <button onClick={logout} data-testid="signout-btn" className="flex items-center gap-2 rounded-md border border-white/15 px-3 py-2 text-sm text-white/80 hover:border-[#1a3fd1] hover:text-white">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </header>
          <div className="flex flex-1 items-center justify-center px-6" data-testid="no-access-screen">
            <div className="max-w-md text-center">
              <img src={LOGO} alt="Elite Mechanical" className="logo-dark-only mx-auto mb-8 h-24 w-auto object-contain opacity-90" />
              <img src={LOGO_LIGHT} alt="Elite Mechanical" className="logo-light-only mx-auto mb-8 h-24 w-auto object-contain opacity-90" />
              <p className="font-display text-xl font-bold uppercase tracking-tight text-white">
                {isCustomerWaiting ? "No project assigned yet" : "Please contact tech support for role assignment"}
              </p>
              <a href="tel:7055626494" className="mt-3 block font-mono text-2xl font-bold tracking-widest text-[#8fa4f0] hover:text-white">
                705-562-6494
              </a>
            </div>
          </div>
          <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
        </div>
      );
    }
  }

  // ---- Customer portal ----
  if (role === "customer") {
    return (
      <div className="flex h-screen flex-col bg-[#050505] text-white" data-testid="dashboard">
        <ImpersonationBanner />
        <WorkgroupBar />
        <header id="app-header" className="flex shrink-0 items-center justify-between border-b border-white/10 bg-[#0a0a0a] px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6">
          <img src={LOGO} alt="Elite Mechanical" className="logo-dark-only h-9 w-auto object-contain" data-testid="topbar-logo" />
          <img src={LOGO_LIGHT} alt="Elite Mechanical" className="logo-light-only h-9 w-auto object-contain" data-testid="topbar-logo-light" />
          <button onClick={logout} data-testid="signout-btn" className="flex items-center gap-2 rounded-md border border-white/15 px-3 py-2 text-sm text-white/80 hover:border-[#1a3fd1] hover:text-white">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </header>
        <div id="dashboard-scroll" className="min-h-0 flex-1 overflow-y-auto">
          <CustomerPortal projectId={user.customer_project_id} />
        </div>
      </div>
    );
  }

  // ---- Standard role-gated dashboard ----
  return (
    <ComposeProvider>
    <BrowserAccessContext.Provider value={!!browserEnabled}>
      <div className="flex h-screen flex-col overflow-hidden bg-[#050505] text-white" data-testid="dashboard" data-tab={activeTab} data-wg={wg?.slug || "elite"}>
      <ImpersonationBanner />
        <WorkgroupBar />
      <header id="app-header" className="flex shrink-0 items-center justify-between border-b border-white/10 bg-[#0a0a0a] px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-6">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Elite Mechanical" className="logo-dark-only h-9 w-auto object-contain" data-testid="topbar-logo" />
          <img src={LOGO_LIGHT} alt="Elite Mechanical" className="logo-light-only h-9 w-auto object-contain" data-testid="topbar-logo-light" />
          <span className="hidden font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 md:block">Operations Portal</span>
        </div>
        <div id="header-actions" className="flex items-center gap-1.5">
          {perms.hours && (
            <button onClick={() => changeTab("hours")} data-testid="tab-hours" aria-label="Employee Hours" title="Hours" className={iconBtn(activeTab === "hours")}>
              <Clock className="h-4 w-4" />
            </button>
          )}
          {perms.payroll && (
            <button onClick={() => changeTab("payroll")} data-testid="tab-payroll" aria-label="Payroll" title="Payroll" className={iconBtn(activeTab === "payroll")}>
              <DollarSign className="h-4 w-4" />
            </button>
          )}
          {browserEnabled && (
            <button onClick={() => changeTab("browser")} data-testid="tab-browser" aria-label="Web browser" title="Browser" className={iconBtn(activeTab === "browser")}>
              <Globe className="h-4 w-4" />
            </button>
          )}
          {/* Accounting moved from the icon rail into the main tab bar next to Projects (user decision 2026-11-02) */}
          {perms.leads && (
            <button onClick={() => changeTab("leads")} data-testid="tab-leads" aria-label="Leads pipeline" title="Leads" className={iconBtn(activeTab === "leads")}>
              <TrendingUp className="h-4 w-4" />
            </button>
          )}
          {perms.files && (
            <button onClick={() => changeTab("files")} data-testid="tab-files" aria-label="Files" title="Files" className={iconBtn(activeTab === "files")}>
              <FolderClosed className="h-4 w-4" />
            </button>
          )}
          {/* Employees + Email moved into the settings dropdown (user decision 2026-11-01) */}
          {/* chat — everyone, always */}
          <button onClick={() => { setChatOpen(true); setChatMin(false); }} data-testid="chat-btn" aria-label="Chat" title="Chat" className={`${iconBtn(false)} relative`}>
            <MessageCircle className="h-4 w-4" />
            {chatUnread > 0 && (
              <span
                data-testid="chat-unread-badge"
                className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#1a8cff] px-1 text-[9px] font-bold leading-none text-[#ffffff]"
              >
                {chatUnread > 99 ? "99+" : chatUnread}
              </span>
            )}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="settings-btn" aria-label="Main menu" title="Menu" className="relative flex h-8 w-8 items-center justify-center rounded-md border border-white/15 text-white/70 transition-colors duration-300 hover:border-[#1a3fd1] hover:text-white data-[state=open]:border-[#1a3fd1] data-[state=open]:text-white">
                <Menu className="h-4 w-4" />
                {perms.email && unreadMail > 0 && (
                  <span data-testid="settings-mail-dot" title={`${unreadMail} unread email`} className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full border border-[#0a0a0a] bg-red-500" />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52" data-testid="settings-menu">
              <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Menu</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => setThemeOpen(true)} data-testid="menu-settings" className="cursor-pointer">
                <SunMoon className="mr-2 h-4 w-4" /> Theme
              </DropdownMenuItem>
              {perms.contacts && (
                <DropdownMenuItem onSelect={() => changeTab("contacts")} data-testid="menu-contacts" className="cursor-pointer">
                  <Contact className="mr-2 h-4 w-4 text-amber-500" /> Contacts
                  {dupCount > 0 && (
                    <span data-testid="vendors-dup-dot" title={`${dupCount} duplicate business group(s) — tap to clean up`} className="ml-auto h-2 w-2 rounded-full bg-red-500" />
                  )}
                </DropdownMenuItem>
              )}
              {perms.employees && (
                <DropdownMenuItem onSelect={() => changeTab("employees")} data-testid="menu-employees" className="cursor-pointer">
                  <UserRound className="mr-2 h-4 w-4 text-violet-500" /> Employees
                </DropdownMenuItem>
              )}
              {perms.email && (
                <DropdownMenuItem onSelect={() => changeTab("email")} data-testid="menu-email" className="cursor-pointer">
                  <Mail className="mr-2 h-4 w-4 text-rose-500" /> Email
                  {unreadMail > 0 && (
                    <span data-testid="mail-unread-badge" className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-[#ffffff]">
                      {unreadMail > 99 ? "99+" : unreadMail}
                    </span>
                  )}
                </DropdownMenuItem>
              )}
              {(role === "superadmin" || perms.billing) && (
                <DropdownMenuItem onSelect={() => changeTab("quickbooks")} data-testid="menu-quickbooks" className="cursor-pointer">
                  <BookOpen className="mr-2 h-4 w-4 text-emerald-600" /> QuickBooks
                </DropdownMenuItem>
              )}
              {canDatabase && (
                <DropdownMenuItem onSelect={() => changeTab("search")} data-testid="menu-database" className="cursor-pointer">
                  <Search className="mr-2 h-4 w-4 text-sky-500" /> Database
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => setHelpOpen(true)} data-testid="menu-help" className="cursor-pointer">
                <LifeBuoy className="mr-2 h-4 w-4" /> Help &amp; Support
              </DropdownMenuItem>
              {!installed && (
                <DropdownMenuItem
                  onSelect={() => openInstallPrompt()}
                  data-testid="menu-install-app"
                  className="cursor-pointer"
                >
                  <Download className="mr-2 h-4 w-4" /> Install app
                </DropdownMenuItem>
              )}
              {role === "superadmin" && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setSettingsOpen(true)} data-testid="menu-admin-settings" className="cursor-pointer">
                    <SlidersHorizontal className="mr-2 h-4 w-4" /> Admin Settings
                  </DropdownMenuItem>
                </>
              )}
              {user?.manage_users && !user?.impersonating && recentImps.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                    Recently impersonated
                  </DropdownMenuLabel>
                  {recentImps.map((r) => (
                    <DropdownMenuItem
                      key={r.user_id}
                      data-testid={`quick-impersonate-${r.user_id}`}
                      disabled={impersonatingId === r.user_id}
                      onSelect={(e) => { e.preventDefault(); quickImpersonate(r); }}
                      className="cursor-pointer"
                    >
                      <UserCog className="mr-2 h-4 w-4 text-[#4f6fe8]" />
                      <span className="flex-1 min-w-0 truncate">
                        <span className="block truncate">{r.name || r.email}</span>
                        <span className="block truncate font-mono text-[10px] text-white/40">{r.role}</span>
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          forgetImpersonation(r.user_id);
                          refreshRecent();
                        }}
                        data-testid={`forget-impersonate-${r.user_id}`}
                        aria-label="Remove from recent"
                        className="ml-2 rounded p-0.5 text-white/40 hover:bg-white/10 hover:text-white"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => logout()}
                data-testid="menu-signout"
                className="cursor-pointer text-red-500 focus:text-red-500 dark:text-red-400 dark:focus:text-red-400"
              >
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <OfflineBanner />

      <Tabs id="main-tabs" value={activeTab} onValueChange={changeTab} className="flex min-h-0 flex-1 flex-col">
        <div id="main-tabs-bar" className="shrink-0 border-b border-white/10 bg-[#0a0a0a] px-2 sm:px-4">
          <TabsList className="flex h-auto w-full gap-0.5 bg-transparent p-0 sm:gap-1">
            <TabsTrigger
              value="stats"
              data-testid="tab-stats"
              aria-label="Home"
              title="Home"
              className={`shrink-0 rounded-none border-b-2 border-transparent px-2 py-3 text-white/50 data-[state=active]:border-[#1a3fd1] data-[state=active]:bg-transparent data-[state=active]:text-[#4f6fe8] data-[state=active]:shadow-none sm:px-3`}
            >
              <Home className="h-4 w-4" />
            </TabsTrigger>
            {visibleTabs.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`} className={`${tabCls} relative`}>
                {t.label}
                {t.key === "contacts" && dupCount > 0 && (
                  <span
                    data-testid="vendors-dup-dot"
                    title={`${dupCount} duplicate business group(s) — tap to clean up`}
                    className="absolute right-1 top-1.5 flex h-2 w-2"
                  >
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400/70" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {perms.files && activeTab === "files" && <SystemFolderBar />}

        <div id="dashboard-scroll" className="min-h-0 flex-1 overflow-y-auto">
          <TabsContent value="stats" className="mt-0">
            <HomeTab />
          </TabsContent>
          {perms.projects && (
            <TabsContent value="projects" className="mt-0">
              <ProjectsTab openProjectId={pendingProjectId} onProjectConsumed={() => setPendingProjectId(null)} />
            </TabsContent>
          )}
          {perms.contacts && (
            <TabsContent value="contacts" className="mt-0">
              <ContactsTab perms={perms} onOpenProject={openProject} initialView={legacyContactsView} />
            </TabsContent>
          )}
          {perms.billing && <TabsContent value="billing" className="mt-0"><BillingTab onOpenProject={openProject} /></TabsContent>}
          {perms.payroll && (
            <TabsContent value="payroll" className="mt-0">
              <PayrollTab />
            </TabsContent>
          )}
          {browserEnabled && (
            <TabsContent value="browser" className="mt-0 p-2 sm:p-4">
              <BrowserTab />
            </TabsContent>
          )}
          {perms.expenses && (
            <TabsContent value="expenses" className="mt-0">
              <ExpensesTab />
            </TabsContent>
          )}
          {(perms.estimate || perms.estimating2) && (
            <TabsContent value="estimate" className="mt-0 h-full data-[state=inactive]:hidden">
              {(() => {
                const effective = aiEstimateRequest ? "1" : (role === "superadmin" && estimateOverride ? estimateOverride : estimateVersion);
                return (
                  <>
                    {role === "superadmin" && (
                      <div className="mb-2 flex items-center justify-end gap-2" data-testid="estimate-version-toggle">
                        {estimateOverride && estimateOverride !== estimateVersion && (
                          <span className="font-mono text-[9px] uppercase tracking-widest text-gray-400">
                            preview only — company is on {estimateVersion === "1" ? "AI" : "New"}
                          </span>
                        )}
                        <div className="inline-flex rounded-full border border-gray-200 bg-white p-0.5 shadow-sm">
                          {[["1", "AI"], ["2", "New"]].map(([v, label]) => (
                            <button key={v} type="button" onClick={() => setEstimateOverride(v)}
                              data-testid={`estimate-version-${v}`}
                              title="Only you see this — it doesn't change the company setting"
                              className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${
                                effective === v ? "bg-[#1a3fd1] text-white" : "text-gray-500 hover:text-gray-800"
                              }`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {effective === "1" ? <EstimateTab /> : <TakeoffTab />}
                  </>
                );
              })()}
            </TabsContent>
          )}
          {perms.design && <TabsContent value="design" className="mt-0 h-full data-[state=inactive]:hidden"><DesignTab /></TabsContent>}
          {perms.accounting && <TabsContent value="accounting" className="mt-0"><AccountingTab scope="company" /></TabsContent>}
          {perms.employees && <TabsContent value="employees" className="mt-0"><EmployeesTab /></TabsContent>}
          {perms.email && <TabsContent value="email" className="mt-0"><EmailTab onOpenSettings={() => setSettingsOpen(true)} /></TabsContent>}
          {perms.files && <TabsContent value="files" className="mt-0"><FilesHub /></TabsContent>}
          {(role === "superadmin" || perms.billing) && (
            <TabsContent value="quickbooks" className="mt-0"><QuickBooksTab /></TabsContent>
          )}
          <TabsContent value="hours" className="mt-0"><HoursTab /></TabsContent>
          {(perms.hours || canDatabase) && <TabsContent value="search" className="mt-0"><SearchTab me={user} /></TabsContent>}
          {perms.leads && (
            <TabsContent value="leads" className="mt-0 h-full data-[state=inactive]:hidden">
              {/* Leads module (contained Steeltooth port) — opens in the
                  main app area. .leads-port scopes the h-screen clamp in
                  index.css so the module fills the tab, not the viewport. */}
              <div className="leads-port h-full min-h-0" data-testid="leads-tab">
                <LeadsModPage />
              </div>
            </TabsContent>
          )}
        </div>

        {/* chat dock — full-screen on phones, Messenger-style bottom-right
            dock on desktop. Open for every role. Clicking anywhere outside
            it (desktop) auto-minimizes to the floating bar so the app stays
            usable; state survives tab changes either way. */}
        {chatOpen && (
          <>
            {!chatMin && (
              <div
                className="fixed inset-0 z-[65] hidden sm:block"
                onClick={() => setChatMin(true)}
                data-testid="chat-min-backdrop"
                aria-hidden="true"
              />
            )}
            <div
              className={chatMin
                ? "fixed bottom-0 left-0 right-0 z-[70] pb-[env(safe-area-inset-bottom)] sm:left-auto sm:right-4 sm:w-72 sm:pb-0"
                : "fixed inset-0 z-[70] flex flex-col overflow-hidden bg-white pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] pt-[env(safe-area-inset-top)] sm:inset-auto sm:bottom-4 sm:right-4 sm:h-[min(660px,85vh)] sm:w-[400px] sm:rounded-xl sm:border sm:border-gray-200 sm:pl-0 sm:pr-0 sm:pt-0 sm:shadow-2xl"}
              style={(!chatMin && chatVV && typeof window !== "undefined" && window.innerWidth < 640)
                ? { top: chatVV.offsetTop, height: chatVV.height, bottom: "auto",
                    left: chatVV.offsetLeft, width: chatVV.width, right: "auto" }
                : undefined}
              data-testid="chat-panel"
            >
              <ChatPanel
                me={user}
                minimized={chatMin}
                openConvoId={chatTarget}
                onOpenHandled={() => setChatTarget(null)}
                onMinimize={() => { setChatMin(true); resetPageZoom(); }}
                onExpand={() => setChatMin(false)}
                onClose={() => { setChatOpen(false); setChatMin(false); resetPageZoom(); }}
                onSeen={(n) => { setChatUnread(n); if (chatMin) refreshChatUnread.current(); }}
              />
            </div>
          </>
        )}

        {/* calls: incoming ring + in-call overlay, always listening */}
        <CallManager me={user} />
        <ChatHeads items={chatHeads} onOpen={openChatHead} chatOpen={chatOpen} chatMin={chatMin} />
        <LiveViewRecorder enabled={!!user} />
      </Tabs>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ThemeSettingsDialog open={themeOpen} onOpenChange={setThemeOpen} />
      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
      </div>
    </BrowserAccessContext.Provider>
    </ComposeProvider>
  );
};

export default Dashboard;
